import axios from 'axios'
import { appConfig } from '../config'

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`
})

const base = () => `${appConfig.apiURL}/api/socr`

const unwrapError = (err, fallback) => {
  const status = err?.response?.status
  const data = err?.response?.data
  const detail =
    (typeof data === 'string' && data) ||
    data?.title ||
    data?.detail ||
    data?.message ||
    (Array.isArray(data?.errors) ? data.errors.join('; ') : null) ||
    err?.message
  const message = detail || fallback
  const error = new Error(message)
  error.status = status
  error.payload = data
  return error
}

const request = async (token, config, fallback) => {
  try {
    const res = await axios({
      ...config,
      headers: {
        ...authHeaders(token),
        ...(config.headers || {})
      }
    })
    return res.data
  } catch (err) {
    throw unwrapError(err, fallback)
  }
}

/** Strip Bitbucket UUID braces for compare/store. */
export const normalizeRepoUuid = (uuid) => String(uuid || '').replace(/[{}]/g, '')

/** GET /api/socr/clients?q= */
export const listSocrClients = (token, { q } = {}) =>
  request(
    token,
    {
      method: 'GET',
      url: `${base()}/clients`,
      params: q ? { q } : undefined
    },
    'Failed to list clients'
  )

/** GET /api/socr/clients/{id} */
export const getSocrClient = (token, id) =>
  request(
    token,
    { method: 'GET', url: `${base()}/clients/${encodeURIComponent(id)}` },
    'Failed to load client'
  )

/** POST /api/socr/clients */
export const createSocrClient = (token, body) =>
  request(
    token,
    { method: 'POST', url: `${base()}/clients`, data: body },
    'Failed to create client'
  )

/** PUT /api/socr/clients/{id} */
export const updateSocrClient = (token, id, body) =>
  request(
    token,
    {
      method: 'PUT',
      url: `${base()}/clients/${encodeURIComponent(id)}`,
      data: body
    },
    'Failed to update client'
  )

/** DELETE /api/socr/clients/{id} */
export const deleteSocrClient = (token, id) =>
  request(
    token,
    {
      method: 'DELETE',
      url: `${base()}/clients/${encodeURIComponent(id)}`
    },
    'Failed to delete client'
  )

/** PUT /api/socr/clients/{id}/jira — full replace */
export const replaceSocrJira = (token, id, links) =>
  request(
    token,
    {
      method: 'PUT',
      url: `${base()}/clients/${encodeURIComponent(id)}/jira`,
      data: links
    },
    'Failed to update Jira links'
  )

/** PUT /api/socr/clients/{id}/bitbucket — full replace */
export const replaceSocrBitbucket = (token, id, links) =>
  request(
    token,
    {
      method: 'PUT',
      url: `${base()}/clients/${encodeURIComponent(id)}/bitbucket`,
      data: links
    },
    'Failed to update Bitbucket links'
  )

/** PUT /api/socr/clients/{id}/qbo — full replace */
export const replaceSocrQbo = (token, id, customers) =>
  request(
    token,
    {
      method: 'PUT',
      url: `${base()}/clients/${encodeURIComponent(id)}/qbo`,
      data: customers
    },
    'Failed to update QBO links'
  )

/** PUT /api/socr/clients/{id}/google — full replace */
export const replaceSocrGoogle = (token, id, projectIds) =>
  request(
    token,
    {
      method: 'PUT',
      url: `${base()}/clients/${encodeURIComponent(id)}/google`,
      data: projectIds
    },
    'Failed to update Google links'
  )

/** GET /api/socr/relationships?type= */
export const listSocrRelationships = (token, { type } = {}) =>
  request(
    token,
    {
      method: 'GET',
      url: `${base()}/relationships`,
      params: type ? { type } : undefined
    },
    'Failed to list relationships'
  )

/** POST /api/socr/relationships */
export const createSocrRelationship = (token, body) =>
  request(
    token,
    { method: 'POST', url: `${base()}/relationships`, data: body },
    'Failed to create relationship'
  )

/** DELETE /api/socr/relationships/{id} */
export const deleteSocrRelationship = (token, id) =>
  request(
    token,
    {
      method: 'DELETE',
      url: `${base()}/relationships/${encodeURIComponent(id)}`
    },
    'Failed to delete relationship'
  )

/** GET /api/socr/repositories?q= — inventory for Bitbucket mapping */
export const listSocrRepositories = (token, { q } = {}) =>
  request(
    token,
    {
      method: 'GET',
      url: `${base()}/repositories`,
      params: q ? { q } : undefined
    },
    'Failed to list repositories'
  )

/**
 * POST /api/socr/repositories/refresh-dt
 * One DT fetch → sync repository_dt_projects for all linked repos (scope: "all").
 */
export const refreshSocrDtProjects = (token) =>
  request(
    token,
    {
      method: 'POST',
      url: `${base()}/repositories/refresh-dt`
    },
    'Failed to refresh DT projects into SOCR'
  )

/**
 * POST /api/socr/repositories/{uuid}/refresh-dt
 * Same sync as refresh-all, scoped to one catalog repo (scope: "one"). 404 if UUID unknown.
 */
export const refreshSocrRepositoryDt = (token, uuid) =>
  request(
    token,
    {
      method: 'POST',
      url: `${base()}/repositories/${encodeURIComponent(normalizeRepoUuid(uuid))}/refresh-dt`
    },
    'Failed to refresh DT projects for repository'
  )

/**
 * POST /api/socr/repositories/refresh-bitbucket
 * Pull Bitbucket metadata + default-branch .NET runtimes for every SOCR catalog repo (scope: "all").
 */
export const refreshSocrBitbucket = (token) =>
  request(
    token,
    {
      method: 'POST',
      url: `${base()}/repositories/refresh-bitbucket`
    },
    'Failed to refresh Bitbucket repositories into SOCR'
  )

/**
 * POST /api/socr/repositories/{uuid}/refresh-bitbucket
 * Same as refresh-all, scoped to one catalog repo (scope: "one"). 404 if UUID unknown.
 */
export const refreshSocrRepositoryBitbucket = (token, uuid) =>
  request(
    token,
    {
      method: 'POST',
      url: `${base()}/repositories/${encodeURIComponent(normalizeRepoUuid(uuid))}/refresh-bitbucket`
    },
    'Failed to refresh Bitbucket repository'
  )

/** POST /api/socr/repositories/resolve — Bitbucket URL → catalog upsert */
export const resolveSocrRepository = (token, { url }) =>
  request(
    token,
    {
      method: 'POST',
      url: `${base()}/repositories/resolve`,
      data: { url }
    },
    'Failed to resolve Bitbucket repository'
  )

/** POST /api/socr/jira/resolve — Jira URL or bare key */
export const resolveSocrJira = (token, { url }) =>
  request(
    token,
    {
      method: 'POST',
      url: `${base()}/jira/resolve`,
      data: { url }
    },
    'Failed to resolve Jira project'
  )

/** GET /api/socr/repositories/{uuid} */
export const getSocrRepository = (token, uuid) =>
  request(
    token,
    {
      method: 'GET',
      url: `${base()}/repositories/${encodeURIComponent(String(uuid || '').replace(/[{}]/g, ''))}`
    },
    'Failed to load repository'
  )

/** GET /api/socr/repositories/{uuid}/branches */
export const listRepoBranches = (token, uuid) =>
  request(
    token,
    {
      method: 'GET',
      url: `${base()}/repositories/${encodeURIComponent(String(uuid || '').replace(/[{}]/g, ''))}/branches`
    },
    'Failed to list branches'
  )

/** GET /api/socr/repositories/{uuid}/branches/{branch}/pipeline-config */
export const getBranchPipelineConfig = (token, uuid, branch) =>
  request(
    token,
    {
      method: 'GET',
      url: `${base()}/repositories/${encodeURIComponent(String(uuid || '').replace(/[{}]/g, ''))}/branches/${encodeURIComponent(branch)}/pipeline-config`
    },
    'Failed to load pipeline config'
  )

/**
 * POST /api/socr/repositories/{uuid}/dt-search
 * @param {{ branch: string, createMissing?: boolean, updateInventory?: boolean }} body
 */
export const searchRepoDt = (token, uuid, body) =>
  request(
    token,
    {
      method: 'POST',
      url: `${base()}/repositories/${encodeURIComponent(String(uuid || '').replace(/[{}]/g, ''))}/dt-search`,
      data: body
    },
    'Failed to search DT projects for branch'
  )

/** POST /api/socr/repositories/{uuid}/pipelines/trigger — kick Bitbucket CI SBOM build */
export const triggerRepoPipeline = (token, uuid, { branch }) =>
  request(
    token,
    {
      method: 'POST',
      url: `${base()}/repositories/${encodeURIComponent(String(uuid || '').replace(/[{}]/g, ''))}/pipelines/trigger`,
      data: { branch }
    },
    'Failed to trigger pipeline'
  )

export const RELATIONSHIP_TYPES = [
  { value: 'shared_codebase', label: 'Shared codebase' },
  { value: 'shared_billing', label: 'Shared billing' },
  { value: 'shared_google', label: 'Shared Google' }
]
